import React, { useState } from 'react';
import SEO from '../../components/SEO';
import Breadcrumb from '../../components/Breadcrumb';
import insuranceData from '../../data/insurance.json';

const InsuranceComparison = () => {
  const [insurance, setInsurance] = useState(insuranceData);
  const [sortBy, setSortBy] = useState('rating');
  const [filterBy, setFilterBy] = useState('all');

  const handleSort = (criteria) => {
    setSortBy(criteria);
    const sorted = [...insurance].sort((a, b) => {
      switch (criteria) {
        case 'rating':
          return b.rating - a.rating;
        case 'premium':
          return parseFloat(a.premium.split(' - ')[0].replace(/[₹,]/g, '')) - parseFloat(b.premium.split(' - ')[0].replace(/[₹,]/g, ''));
        case 'sumAssured':
          return parseFloat(b.sumAssured.split(' - ')[1].replace(/[₹,]/g, '')) - parseFloat(a.sumAssured.split(' - ')[1].replace(/[₹,]/g, ''));
        default:
          return 0;
      }
    });
    setInsurance(sorted);
  };

  const handleFilter = (filter) => {
    setFilterBy(filter);
    if (filter === 'all') {
      setInsurance(insuranceData);
    } else if (filter === 'termInsurance') {
      setInsurance(insuranceData.filter(plan => plan.type === 'Term Insurance'));
    } else if (filter === 'healthInsurance') {
      setInsurance(insuranceData.filter(plan => plan.type === 'Health Insurance'));
    } else if (filter === 'carInsurance') {
      setInsurance(insuranceData.filter(plan => plan.type === 'Car Insurance'));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Term Insurance':
        return '🛡️';
      case 'Health Insurance':
        return '🏥';
      case 'Car Insurance':
        return '🚗';
      default:
        return '📋';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Top Insurance Plans Comparison"
        description="Compare health insurance, life insurance, and car insurance plans from top providers. Find the best insurance coverage for your needs. Insurance comparison India."
        keywords="health insurance comparison, life insurance plans, car insurance quotes, insurance coverage, term insurance, insurance comparison India, best insurance 2025"
        type="website"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Comparisons', url: '/comparisons' },
          { name: 'Insurance Comparison', url: '/comparisons/insurance' }
        ]}
      />
      <Breadcrumb
        items={[
          { name: 'Home', url: '/' },
          { name: 'Insurance Comparison', url: '/comparisons/insurance' }
        ]}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Top Insurance Plans Comparison
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Compare health insurance, life insurance, and car insurance plans from top providers. Find the best coverage for your needs.
          </p>
        </div>

        {/* Filters and Sort */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Filter by:</label>
              <select
                value={filterBy}
                onChange={(e) => handleFilter(e.target.value)}
                className="input-field"
              >
                <option value="all">All Insurance</option>
                <option value="termInsurance">Term Insurance</option>
                <option value="healthInsurance">Health Insurance</option>
                <option value="carInsurance">Car Insurance</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sort by:</label>
              <select
                value={sortBy}
                onChange={(e) => handleSort(e.target.value)}
                className="input-field"
              >
                <option value="rating">Rating</option>
                <option value="premium">Premium</option>
                <option value="sumAssured">Sum Assured</option>
              </select>
            </div>
          </div>
        </div>

        {/* Insurance Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {insurance.map((plan) => (
            <div key={plan.id} className="card hover:shadow-xl transition-all duration-300">
              {/* Plan Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">{getTypeIcon(plan.type)}</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{plan.name}</h3>
                    <p className="text-sm text-gray-600">{plan.company}</p>
                    <span className="inline-block px-2 py-1 text-xs bg-primary-100 text-primary-600 rounded-full mt-1">
                      {plan.type}
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-1">
                  <span className="text-yellow-400">★</span>
                  <span className="text-sm font-medium text-gray-700">{plan.rating}</span>
                </div>
              </div>

              {/* Key Features */}
              <div className="space-y-3 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Premium</span>
                  <span className="font-semibold text-primary-600">{plan.premium}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Sum Assured</span>
                  <span className="font-semibold text-gray-900">{plan.sumAssured}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Coverage</span>
                  <span className="font-semibold text-gray-900">{plan.coverage}</span>
                </div>
              </div>

              {/* Features List */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-2">Key Features:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  {plan.features.slice(0, 3).map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Eligibility */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-2">Eligibility:</h4>
                <p className="text-sm text-gray-600">{plan.eligibility}</p>
              </div>

              {/* Action Button */}
              <button className="w-full btn-primary">
                Get Quote
              </button>
            </div>
          ))}
        </div>

        {/* Educational Content */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              💡 Insurance Tips
            </h3>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• Buy insurance early for lower premiums</li>
              <li>• Compare coverage and exclusions</li>
              <li>• Check claim settlement ratio</li>
              <li>• Read policy documents carefully</li>
            </ul>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              🏥 Health Insurance
            </h3>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• Cashless treatment at network hospitals</li>
              <li>• Pre and post hospitalization coverage</li>
              <li>• Day care procedures covered</li>
              <li>• Tax benefits under Section 80D</li>
            </ul>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              🛡️ Life Insurance
            </h3>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• Term insurance for pure protection</li>
              <li>• Whole life for lifelong coverage</li>
              <li>• ULIP for investment + protection</li>
              <li>• Tax benefits under Section 80C</li>
            </ul>
          </div>
        </div>

      </div>
    </div>
  );
};

export default InsuranceComparison;
